import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Users,
  CalendarDays,
  Network,
  FolderOpen,
  Building2,
  ClipboardCheck,
  Download,
  UserCircle,
} from 'lucide-react'
import { useEmployee, useHrContext } from '@/hooks/use-api'
import { useUser } from '@/contexts/UserContext'

function initials(first?: string | null, last?: string | null): string {
  return `${(first ?? '').charAt(0)}${(last ?? '').charAt(0)}`.toUpperCase() || '?'
}

interface Tile {
  label: string
  description: string
  href: string
  icon: typeof Users
}

export default function HrHome() {
  const { currentUser } = useUser()
  const { data: ctx, isLoading: ctxLoading } = useHrContext()
  const empQuery = useEmployee(ctx?.employeeId ?? '')
  const me = empQuery.data?.data

  const isHrAdmin = ctx?.isHrAdmin ?? false
  const isManager = ctx?.isManager ?? false
  const noRecord = !ctxLoading && !ctx?.employeeId
  type TeamMember = {
    id: string
    first_name: string
    last_name: string
    job_title?: string | null
    profile_photo_url?: string | null
    note?: string | null
  }
  const reports = (me?.direct_reports ?? []) as TeamMember[]
  const dottedReports = (me?.dotted_reports ?? []) as TeamMember[]

  const tiles: Tile[] = [
    {
      label: 'Org Chart',
      description: 'See how the company is organised',
      href: '/hr/org-chart',
      icon: Network,
    },
    {
      label: 'Time Off',
      description: 'Book leave and see your balance',
      href: '/hr/time-off',
      icon: CalendarDays,
    },
    {
      label: 'Directory',
      description: isManager ? 'You and your team' : 'Your profile',
      href: '/hr/employees',
      icon: Users,
    },
  ]
  if (isManager) {
    tiles.push({
      label: 'Leave Approvals',
      description: "Review your team's requests",
      href: '/hr/leave-approvals',
      icon: ClipboardCheck,
    })
  }
  const adminTiles: Tile[] = isHrAdmin
    ? [
        {
          label: 'Employees',
          description: 'Manage all employee records',
          href: '/hr/employees',
          icon: Users,
        },
        {
          label: 'Departments',
          description: 'Manage departments',
          href: '/hr/departments',
          icon: Building2,
        },
        {
          label: 'Company Documents',
          description: 'Handbook, templates, H&S',
          href: '/hr/company-documents',
          icon: FolderOpen,
        },
      ]
    : []

  const displayName = me
    ? `${me.preferred_name || me.first_name} ${me.last_name}`.trim()
    : currentUser?.name || 'there'

  return (
    <main className="container mx-auto max-w-5xl p-6">
      <header className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <UserCircle className="h-7 w-7" aria-hidden="true" />
          Welcome, {displayName}
        </h1>
        <p className="text-sm text-muted-foreground">
          Your HR home — profile, team and self-service.
        </p>
      </header>

      {/* Profile summary */}
      <Card className="mb-4">
        <CardContent className="flex items-center gap-4 pt-6">
          {me?.profile_photo_url ? (
            <img
              src={me.profile_photo_url}
              alt=""
              className="h-16 w-16 rounded-full object-cover"
            />
          ) : (
            <div
              className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary"
              aria-hidden="true"
            >
              {initials(me?.first_name, me?.last_name)}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold">{displayName}</p>
            {me?.job_title && <p className="text-sm text-muted-foreground">{me.job_title}</p>}
            <div className="mt-1 flex flex-wrap gap-2">
              {me?.department_name && <Badge variant="secondary">{me.department_name}</Badge>}
              {isHrAdmin && <Badge>HR Admin</Badge>}
              {isManager && !isHrAdmin && <Badge variant="outline">Manager</Badge>}
            </div>
          </div>
          {me && (
            <Link to="/hr/me" className="text-sm text-primary underline underline-offset-2">
              View my profile
            </Link>
          )}
        </CardContent>
      </Card>

      {noRecord && (
        <Card className="mb-4">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            We couldn’t find an employee record linked to your account. Please contact HR.
          </CardContent>
        </Card>
      )}

      {/* Quick links */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {tiles.map((t) => (
          <Link
            key={t.label}
            to={t.href}
            className="block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Card className="h-full transition-colors hover:border-primary/40 hover:bg-muted">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <t.icon className="h-5 w-5 text-primary" aria-hidden="true" />
                  {t.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>{t.description}</CardDescription>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* My team */}
      {reports.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-[#072744]">My team</h2>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {reports.map((r) => (
              <Link
                key={r.id}
                to={`/hr/employees/${r.id}`}
                className="flex items-center gap-3 rounded-md border border-border p-2 hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {r.profile_photo_url ? (
                  <img
                    src={r.profile_photo_url}
                    alt=""
                    className="h-9 w-9 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
                    aria-hidden="true"
                  >
                    {initials(r.first_name, r.last_name)}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {r.first_name} {r.last_name}
                  </p>
                  {r.job_title && (
                    <p className="truncate text-xs text-muted-foreground">{r.job_title}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Dotted-line (secondary) reports */}
      {dottedReports.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-[#941052]">
            Dotted-line reports
          </h2>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {dottedReports.map((r) => (
              <Link
                key={r.id}
                to={`/hr/employees/${r.id}`}
                className="flex items-center gap-3 rounded-md border border-dashed border-[#941052]/50 p-2 hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {r.profile_photo_url ? (
                  <img
                    src={r.profile_photo_url}
                    alt=""
                    className="h-9 w-9 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
                    aria-hidden="true"
                  >
                    {initials(r.first_name, r.last_name)}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {r.first_name} {r.last_name}
                  </p>
                  {r.job_title && (
                    <p className="truncate text-xs text-muted-foreground">{r.job_title}</p>
                  )}
                  {r.note && <p className="truncate text-xs text-[#941052]">{r.note}</p>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* HR admin tools */}
      {adminTiles.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-[#072744]">
            HR administration
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {adminTiles.map((t) => (
              <Link
                key={t.label}
                to={t.href}
                className="block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Card className="h-full transition-colors hover:border-primary/40 hover:bg-muted">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <t.icon className="h-5 w-5 text-primary" aria-hidden="true" />
                      {t.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>{t.description}</CardDescription>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
            <Download className="h-3 w-3" aria-hidden="true" />
            Full HR export is available on the Employees page.
          </p>
        </section>
      )}
    </main>
  )
}
